/**
 * SpendBreakdownChart — pure-SVG stacked bar chart
 * =================================================
 * Reads /api/commitment-rebalancer/spend-breakdown-by-day and renders
 * a stacked bar per day. No external charting library (keeps bundle small).
 *
 * Categories: compute (emerald), storage (cyan), egress (amber),
 * refunds (slate-positive, shown above the axis as "saved"),
 * other (slate).
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = [
  { key: 'compute', color: '#10b981', label: 'Compute' },
  { key: 'storage', color: '#06b6d4', label: 'Storage' },
  { key: 'egress',  color: '#f59e0b', label: 'Egress'  },
  { key: 'other',   color: '#64748b', label: 'Other'   },
];

const fmt = (n) => {
  if (!n) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n)}`;
};

const SpendBreakdownChart = ({ days = 30 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  useEffect(() => {
    (async () => {
      if (!token) { setLoading(false); return; }
      try {
        const { data } = await axios.get(`${API_URL}/commitment-rebalancer/spend-breakdown-by-day`, {
          params: { days },
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(data);
      } catch (e) {
        // Silent — chart just won't render
      } finally {
        setLoading(false);
      }
    })();
  }, [days, token]);

  const { maxDailyTotal, totalSavings, totalSpend } = useMemo(() => {
    if (!data?.items?.length) return { maxDailyTotal: 0, totalSavings: 0, totalSpend: 0 };
    let max = 0; let savings = 0; let spend = 0;
    for (const row of data.items) {
      const dayTotal = (row.compute || 0) + (row.storage || 0) + (row.egress || 0) + (row.other || 0);
      if (dayTotal > max) max = dayTotal;
      savings += row.refunds || 0;
      spend += dayTotal;
    }
    return { maxDailyTotal: max, totalSavings: savings, totalSpend: spend };
  }, [data]);

  if (loading) {
    return (
      <div className="text-sm text-slate-600 flex items-center gap-2 py-4">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading chart…
      </div>
    );
  }

  if (!data?.items?.length) return null;  // No data → render nothing

  const CHART_HEIGHT = 180;
  const BAR_GAP = 2;
  const BAR_WIDTH = Math.max(6, Math.floor((100 - BAR_GAP * data.items.length) / data.items.length));

  return (
    <div data-testid="spend-breakdown-chart" className="mt-3">
      <div className="flex items-center justify-between mb-2 text-xs text-slate-600">
        <div>
          <span className="font-medium text-slate-900">Spend by day</span>
          <span className="ml-2">{data.items.length}-day total: <span className="font-medium tabular-nums">{fmt(totalSpend)}</span></span>
        </div>
        {totalSavings > 0 && (
          <div className="text-emerald-700 font-medium" data-testid="chart-refunds-saved">
            Saved from refunds: {fmt(totalSavings)}
          </div>
        )}
      </div>

      <div className="relative bg-slate-50 rounded-md border border-slate-200 p-2" style={{ height: CHART_HEIGHT + 30 }}>
        <svg width="100%" height={CHART_HEIGHT} viewBox={`0 0 100 ${CHART_HEIGHT}`} preserveAspectRatio="none" className="overflow-visible">
          {data.items.map((row, i) => {
            const dayTotal = (row.compute || 0) + (row.storage || 0) + (row.egress || 0) + (row.other || 0);
            const x = i * (BAR_WIDTH + BAR_GAP);
            let yCursor = CHART_HEIGHT;
            return (
              <g key={row.date}>
                {CATEGORY_META.map((c) => {
                  const v = row[c.key] || 0;
                  if (v <= 0 || maxDailyTotal === 0) return null;
                  const segHeight = (v / maxDailyTotal) * CHART_HEIGHT;
                  yCursor -= segHeight;
                  return (
                    <rect
                      key={c.key}
                      x={x}
                      y={yCursor}
                      width={BAR_WIDTH}
                      height={segHeight}
                      fill={c.color}
                      data-testid={`chart-bar-${row.date}-${c.key}`}
                    >
                      <title>{`${row.date} · ${c.label}: ${fmt(v)} (day total ${fmt(dayTotal)})`}</title>
                    </rect>
                  );
                })}
              </g>
            );
          })}
        </svg>
        <div className="flex justify-between text-[10px] text-slate-500 mt-1 px-1">
          <span>{data.items[0]?.date}</span>
          <span>{data.items[data.items.length - 1]?.date}</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mt-2 text-xs">
        {CATEGORY_META.map((c) => (
          <div key={c.key} className="inline-flex items-center gap-1.5" data-testid={`chart-legend-${c.key}`}>
            <span className="inline-block w-3 h-3 rounded-sm" style={{ background: c.color }} />
            <span className="text-slate-700">{c.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SpendBreakdownChart;
